import type { BulkUpsertMaterialError, BulkUpsertMaterialsResponse, ImportBody } from './types';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import api from '@/lib/api';

const BULK_UPSERT_PATH = 'danh-muc/vat-tu-san-pham-kho/materials/bulk-upsert';

type ApiErrorLike = {
  response?: {
    data?: unknown;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBulkUpsertError = (value: unknown): value is BulkUpsertMaterialError =>
  isRecord(value) &&
  typeof value.index === 'number' &&
  typeof value.field === 'string' &&
  typeof value.error === 'string';

export const bulkUpsertMaterials = async (
  entitySlug: string,
  list: ImportBody[]
): Promise<BulkUpsertMaterialsResponse> => {
  const response = await api.post<BulkUpsertMaterialsResponse>(`/entities/${entitySlug}/erp/${BULK_UPSERT_PATH}/`, {
    list
  });

  return response.data;
};

export const extractBulkUpsertErrors = (error: unknown): BulkUpsertMaterialError[] => {
  const responseData = (error as ApiErrorLike)?.response?.data;
  if (!isRecord(responseData)) return [];

  const candidates = [responseData.errors, responseData.error];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(isBulkUpsertError);
    }
  }

  return [];
};

export const mapBulkUpsertErrorsToExcel = (
  errors: BulkUpsertMaterialError[],
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] =>
  errors.map(error => {
    const mapping = Object.values(columnMapping).find(column => column.key === error.field);

    return {
      // Excel row 1 is the header; backend index is zero-based.
      row: error.index + 2,
      field: error.field,
      message: error.error,
      section: mapping?.type || 'main',
      columnIndex: mapping?.index
    };
  });
